import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DashService {
  private screenSize = new BehaviorSubject<{ width: number; height: number }>({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  isCollapseMenu: boolean = true;
  private onResize(): void {
    this.screenSize.next({
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }
  screenSize$ = this.screenSize.asObservable();

  constructor() {
    window.addEventListener('resize', this.onResize.bind(this));
  }

  truncateString(str: string, num: number) {
    /**
     * Truncates a string to a specified length and appends an ellipsis if truncated.
     * @param str The string to truncate.
     * @param num The maximum length of the string before truncation.
     * @returns A truncated string with ellipsis if the original string exceeds the specified length.
     */
    if (str.length > num) {
      return str.slice(0, num) + '...'; // Truncate and append ellipsis
    } else {
      return str; // Return original string if no truncation is needed
    }
  }

  formatNumber(num: number) {
    /**
     * Formats a number to a human-readable format.
     * @param num The number to format.
     * @returns A string with the number formatted to a human-readable format.
     *
     * If the number is greater than or equal to 1 billion, it will be formatted to
     * a string with the number divided by 1 billion and suffixed with 'B'.
     *
     * If the number is greater than or equal to 1 million, it will be formatted to
     * a string with the number divided by 1 million and suffixed with 'M'.
     *
     * If the number is greater than or equal to 1 thousand, it will be formatted to
     * a string with the number divided by 1 thousand and suffixed with 'K'.
     *
     * If the number is less than 1 thousand, it will be formatted to a string with
     * the number as is.
     */
    if (num >= 1e9) {
      return (num / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
    } else if (num >= 1e6) {
      return (num / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
    } else if (num >= 1e3) {
      return (num / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
    } else {
      return num ? num.toString() : num;
    }
  }

  formatDate(dateString) {
    /**
     * Formats a date string to "Day MONTH" format.
     * @param dateString The date string to format.
     * @returns A formatted string with the day and month in uppercase.
     */
    const date = new Date(dateString); // Create a Date object from the date string
    const day = date.getDate(); // Extract the day of the month
    // Extract the month in short format and convert to uppercase
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    return `${day} ${month}`; // Return the formatted date string
  }

  getCurrentLocation(): Promise<{ latitude: number; longitude: number }> {
    /**
     * Get the current geographical location.
     * @returns A promise that resolves with an object containing latitude and longitude.
     *          If geolocation is not supported or an error occurs, the promise is rejected.
     */
    return new Promise((resolve, reject) => {
      // Check if geolocation is supported
      if (navigator.geolocation) {
        // Request the current position
        navigator.geolocation.getCurrentPosition(
          position => {
            // Resolve the promise with the current position
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          error => {
            // Reject the promise if there's an error retrieving the position
            reject(error);
          }
        );
      } else {
        // Reject the promise if geolocation is not supported
        reject(new Error('Geolocation is not supported by this browser.'));
      }
    });
  }

  formatDateAndTime(dateString, timeString) {
    // Combine the date and time strings into a single date-time format
    const combinedDateTime = new Date(`${dateString}T${this.convertTo24Hour(timeString)}`);
    // Define options for formatting
    const options: any = { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true };
    // Format the combined date-time
    return combinedDateTime.toLocaleString('en-US', options).replace(',', '');
  }

  // Helper function to convert 12-hour time to 24-hour format
  convertTo24Hour(time) {
    /**
     * Converts a 12-hour time string to a 24-hour time string.
     * @param time The 12-hour time string to convert.
     * @returns The 24-hour time string.
     */
    const [timePart, modifier] = time.split(' ');
    let [hours, minutes] = timePart.split(':');
    // If the time is PM and the hour is not 12, add 12 to the hour
    if (modifier === 'PM' && hours !== '12') hours = (parseInt(hours) + 12).toString();
    // If the time is AM and the hour is 12, set the hour to 00
    if (modifier === 'AM' && hours === '12') hours = '00';
    return `${hours}:${minutes}`;
  }

  getTimeOfDay() {
    /**
     * Get the current time of day.
     * @returns The current time of day as a string. One of "sunrise", "day", "sunset", or "night".
     */
    const currentHour = new Date().getHours();
    if (currentHour >= 5 && currentHour < 7) {
      // Between 5am and 7am
      return 'sunrise';
    } else if (currentHour >= 7 && currentHour < 17) {
      // Between 7am and 5pm
      return 'day';
    } else if (currentHour >= 17 && currentHour < 19) {
      // Between 5pm and 7pm
      return 'sunset';
    } else {
      // Between 7pm and 5am
      return 'night';
    }
  }
}
